import { useEffect, useState } from "react";
import { BellRinging, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { enablePush, isPushSupported } from "../lib/push";

const DISMISS_KEY = "ag_push_banner_dismissed_v1";

/**
 * Sprint G · Batch 2 — Banner contextual de invitación a Push.
 * Visible solo cuando:
 *   - el navegador soporta Web Push
 *   - Notification.permission === "default"  (ni granted ni denied)
 *   - el usuario no lo descartó manualmente (localStorage ag_push_banner_dismissed_v1)
 *
 * Props:
 *   role: "customer" | "user"   (a qué endpoint suscribirse)
 *   variant: "customer" | "shop"  → ajusta el copy
 *   className: clases extra
 */
export function PushInvitationBanner({ role = "customer", variant, className = "" }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (typeof Notification === "undefined") return;
    const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    if (dismissed) return;
    if (Notification.permission === "default") setShow(true);
  }, []);

  if (!show) return null;

  const v = variant || (role === "user" ? "shop" : "customer");
  const copy = v === "shop"
    ? {
        title: "No te pierdas ni una reserva",
        body: "Activa las notificaciones del navegador para recibir un aviso instantáneo en tu móvil u ordenador cuando entre una nueva reserva o llegue un mensaje del cliente — incluso con la app cerrada.",
        cta: "Activar avisos del taller",
      }
    : {
        title: "Chatea en tiempo real con tu taller",
        body: "Activa las notificaciones para recibir una alerta inmediata cuando el taller te escriba, te confirme la cita o tu vehículo esté listo para recoger.",
        cta: "Activar notificaciones",
      };

  const onActivate = async () => {
    setBusy(true);
    try {
      await enablePush({ role });
      toast.success("Notificaciones activadas correctamente.");
      setShow(false);
    } catch (e) {
      toast.error(e?.message || "No se pudieron activar las notificaciones");
      // Si el usuario las denegó, ocultamos el banner para no insistir
      if (typeof Notification !== "undefined" && Notification.permission !== "default") {
        setShow(false);
      }
    } finally { setBusy(false); }
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const isShop = v === "shop";
  // Paleta acorde a cada panel: en la zona del taller (oscura) usamos gold,
  // en la zona del cliente final (clara) usamos azul corporativo.
  return (
    <div
      className={`relative rounded-md border overflow-hidden flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4 p-4 mb-4 ${
        isShop
          ? "bg-[#0F172A] border-[#1F2937] text-[var(--ag-text)] shadow-lg"
          : "bg-blue-50 border-blue-200 text-[#1A1A1A]"
      } ${className}`}
      data-testid={`push-banner-${v}`}
    >
      <div className={`shrink-0 grid place-items-center w-12 h-12 rounded-sm ${
        isShop ? "bg-[#FFB703]/15 text-[#FFB703]" : "bg-[var(--ag-gold)] text-white"
      }`}>
        <BellRinging size={22} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${isShop ? "text-[var(--ag-text)]" : "text-[#1A1A1A]"}`}>{copy.title}</div>
        <p className={`text-xs mt-0.5 ${isShop ? "text-gray-300" : "text-gray-600"}`}>
          {copy.body}
        </p>
      </div>
      <div className="flex items-center gap-2 sm:self-center">
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={onActivate}
          className={isShop
            ? "bg-[#FFB703] text-black hover:opacity-90 rounded-sm"
            : "bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white rounded-sm"}
          data-testid={`push-banner-cta-${v}`}
        >
          {busy ? "Activando…" : copy.cta}
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          className={`p-1.5 rounded ${isShop ? "text-[var(--ag-text-muted)] hover:text-white" : "text-[var(--ag-text-muted)] hover:text-gray-700"}`}
          aria-label="Cerrar"
          data-testid={`push-banner-dismiss-${v}`}
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
